import React from 'react';

export const Card = ({ children, style }) => (
  <div style={Object.assign({ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20 }, style)}>
    {children}
  </div>
);

export const PageTitle = ({ children, sub }) => (
  <div style={{ marginBottom: 24 }}>
    <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>{children}</h1>
    {sub && <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{sub}</p>}
  </div>
);

export const Btn = ({ children, onClick, variant, size, disabled, style, type }) => {
  const v = variant || 'primary';
  const s = size || 'md';
  const t = type || 'button';
  const colors = {
    primary: { background: '#25d366', color: '#000', border: 'none' },
    danger: { background: '#ef4444', color: '#fff', border: 'none' },
    secondary: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155' },
    warning: { background: '#f59e0b', color: '#000', border: 'none' },
    ghost: { background: 'transparent', color: '#64748b', border: 'none' },
  };
  const sizes = {
    sm: { padding: '4px 10px', fontSize: 12 },
    md: { padding: '8px 16px', fontSize: 13 },
    lg: { padding: '10px 20px', fontSize: 14 },
  };
  const cursorStyle = disabled ? 'not-allowed' : 'pointer';
  const baseStyle = {
    borderRadius: 6,
    cursor: cursorStyle,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  };
  return (
    <button
      type={t}
      onClick={onClick}
      disabled={disabled}
      style={Object.assign(baseStyle, colors[v], sizes[s], style)}
    >
      {children}
    </button>
  );
};

export const Input = ({ label, error, style, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{label}</label>}
    <input
      {...props}
      style={Object.assign({
        width: '100%', padding: '9px 12px', background: '#0f172a',
        border: '1px solid ' + (error ? '#ef4444' : '#334155'),
        borderRadius: 6, color: '#e2e8f0', fontSize: 14, outline: 'none',
      }, style)}
    />
    {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{error}</p>}
  </div>
);

export const Select = ({ label, children, style, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{label}</label>}
    <select
      {...props}
      style={Object.assign({
        width: '100%', padding: '9px 12px', background: '#0f172a',
        border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 14, outline: 'none',
      }, style)}
    >
      {children}
    </select>
  </div>
);

export const Textarea = ({ label, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{label}</label>}
    <textarea
      {...props}
      style={Object.assign({
        width: '100%', padding: '9px 12px', background: '#0f172a',
        border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0',
        fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 100,
      }, props.style)}
    />
  </div>
);

export const Badge = ({ children, color }) => {
  const c = color || 'default';
  const colors = {
    default: { background: '#334155', color: '#94a3b8' },
    green: { background: 'rgba(37,211,102,0.15)', color: '#25d366' },
    red: { background: 'rgba(239,68,68,0.15)', color: '#ef4444' },
    yellow: { background: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    blue: { background: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
  };
  return (
    <span style={Object.assign({ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }, colors[c])}>
      {children}
    </span>
  );
};

export const Spinner = ({ size }) => {
  const sz = size || 20;
  return (
    <div style={{
      width: sz, height: sz,
      border: '2px solid #334155',
      borderTop: '2px solid #25d366',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      flexShrink: 0,
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export const StatCard = ({ label, value, sub, icon: Icon, color }) => {
  const c = color || '#25d366';
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ color: '#64748b', fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</p>
          <p style={{ fontSize: 26, fontWeight: 700, color: '#f1f5f9' }}>{value !== undefined ? value : '—'}</p>
          {sub && <p style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{sub}</p>}
        </div>
        {Icon && (
          <div style={{ background: c + '22', padding: 10, borderRadius: 8 }}>
            <Icon size={20} style={{ color: c }} />
          </div>
        )}
      </div>
    </Card>
  );
};

export const Table = ({ headers, rows, loading }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', borderBottom: '1px solid #334155', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={headers.length} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No data found</td>
          </tr>
        ) : rows.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
            {row.map((cell, j) => (
              <td key={j} style={{ padding: '10px 14px', color: '#cbd5e1', verticalAlign: 'middle' }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Modal = ({ open, onClose, title, children, width }) => {
  if (!open) return null;
  const w = width || 500;
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
    >
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, width: '100%', maxWidth: w, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #334155' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
};

export const Pagination = ({ page, pages, onPage }) => {
  if (!pages || pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
      <Btn size="sm" variant="secondary" onClick={() => onPage(page - 1)} disabled={page <= 1}>←</Btn>
      <span style={{ padding: '4px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 13, color: '#94a3b8' }}>
        {page} / {pages}
      </span>
      <Btn size="sm" variant="secondary" onClick={() => onPage(page + 1)} disabled={page >= pages}>→</Btn>
    </div>
  );
};
